import { ContextWithQuery, defaultDereferenceMessages, ExecutePipelineDetails, normalizeMessagesOrName, PipelineDetailsData } from "@me8eon/agent";
import { AiAndToolOptions, AiClients, AiToolsRepository, callAiAndAnyTools } from "@me8eon/aiclient";
import { ErrorsOr, flatMapLogK, LogAnd, mapLog } from "@me8eon/errors";
import { Message } from "@me8eon/messages";

export type LlmPipelineDetails = {
    type: "llm";
    model: string;
    toolSelectionModel?: string;
    prefix?: any;
    temperature?: number;
    tools?: string[];
};

export const executeLlmPipelineDetails = <Context extends ContextWithQuery>(
    aiClients: AiClients,
    toolRepo: AiToolsRepository
): ExecutePipelineDetails<Context, LlmPipelineDetails> =>
    async (p: LlmPipelineDetails, input: PipelineDetailsData<Context>): Promise<LogAnd<ErrorsOr<PipelineDetailsData<Context>>>> => {
        const { context, originalMessages, appendMessages = [], stream } = input;

        return flatMapLogK(
            defaultDereferenceMessages({ context }, normalizeMessagesOrName(p.prefix || [], "system")),
            async prefixMessages => {
                const messages: Message[] = [...originalMessages, ...prefixMessages, ...appendMessages];
                const aiClient = aiClients[p.model];

                if (!aiClient) {
                    throw new Error(`No AI client configured with name '${p.model}'. Legal names are: ${Object.keys(aiClients).sort()}`);
                }

                const options: AiAndToolOptions = {
                    legalTools: p.tools || [],
                    toolRepo,
                    temperature: p.temperature,
                    toolSelectionModel: p.toolSelectionModel,
                    ...(stream?.sink ? { stream: { sink: stream.sink } } : {})
                };

                const response: LogAnd<ErrorsOr<Message[]>> = await callAiAndAnyTools(
                    aiClient,
                    toolRepo,
                    [...messages, { role: "user", content: context.query }],
                    options
                );

                return mapLog(response, value => ({
                    ...input,
                    appendMessages: [...appendMessages, ...value]
                }));
            }
        );
    };